import React, { useState, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { X, ChevronLeft, ChevronRight, HelpCircle } from 'lucide-react';

// Custom Tour Component - React 19 Compatible
const TourOverlay = ({ children }) => {
  return createPortal(
    <div className="fixed inset-0 z-[9999]">
      {children}
    </div>,
    document.body
  );
};

const TourTooltip = ({ step, currentStep, totalSteps, onNext, onPrev, onClose }) => {
  const [tooltipStyle, setTooltipStyle] = useState({});

  useEffect(() => {
    const computePosition = () => {
      const mobile = window.innerWidth < 768;
      const TOOLTIP_WIDTH = mobile ? Math.min(window.innerWidth - 24, 360) : 420;
      const TOOLTIP_MAX_HEIGHT = Math.min(window.innerHeight - 32, 560);
      const MARGIN = 12;

      const clampLeft = (left) =>
        Math.max(12, Math.min(left, window.innerWidth - TOOLTIP_WIDTH - 12));
      const clampTop = (top) =>
        Math.max(12, Math.min(top, window.innerHeight - 80 - 12));

      const baseStyle = {
        position: 'fixed',
        width: `${TOOLTIP_WIDTH}px`,
        maxWidth: 'calc(100vw - 24px)',
        maxHeight: `${TOOLTIP_MAX_HEIGHT}px`,
      };

      // Center placement (welcome / summary / explicit center)
      if (step.target === 'body' || step.placement === 'center') {
        setTooltipStyle({
          ...baseStyle,
          left: `${(window.innerWidth - TOOLTIP_WIDTH) / 2}px`,
          top: `${Math.max(12, (window.innerHeight - 280) / 2)}px`,
        });
        return;
      }

      const element = document.querySelector(step.target);
      if (!element) {
        setTooltipStyle({
          ...baseStyle,
          left: `${(window.innerWidth - TOOLTIP_WIDTH) / 2}px`,
          top: `${Math.max(12, (window.innerHeight - 280) / 2)}px`,
        });
        return;
      }

      const rect = element.getBoundingClientRect();
      // On mobile, sidebar items can be off-screen entirely. Default to bottom-anchored centered modal.
      const placement = mobile ? 'mobile-bottom' : (step.placement || 'bottom');

      const style = { ...baseStyle };

      switch (placement) {
        case 'mobile-bottom': {
          style.left = `${(window.innerWidth - TOOLTIP_WIDTH) / 2}px`;
          // If the target is in the top half, drop tooltip to bottom; else pin to top
          if (rect.top < window.innerHeight / 2) {
            style.bottom = '16px';
          } else {
            style.top = '16px';
          }
          break;
        }
        case 'bottom': {
          style.top = `${clampTop(rect.bottom + MARGIN)}px`;
          style.left = `${clampLeft(rect.left + rect.width / 2 - TOOLTIP_WIDTH / 2)}px`;
          break;
        }
        case 'top': {
          style.bottom = `${window.innerHeight - rect.top + MARGIN}px`;
          style.left = `${clampLeft(rect.left + rect.width / 2 - TOOLTIP_WIDTH / 2)}px`;
          break;
        }
        case 'right': {
          if (rect.right + TOOLTIP_WIDTH + MARGIN > window.innerWidth) {
            style.top = `${clampTop(rect.bottom + MARGIN)}px`;
            style.left = `${clampLeft(rect.left)}px`;
          } else {
            style.top = `${clampTop(rect.top + rect.height / 2 - 80)}px`;
            style.left = `${rect.right + MARGIN}px`;
          }
          break;
        }
        case 'left': {
          if (rect.left - TOOLTIP_WIDTH - MARGIN < 0) {
            style.top = `${clampTop(rect.bottom + MARGIN)}px`;
            style.left = `${clampLeft(rect.left)}px`;
          } else {
            style.top = `${clampTop(rect.top + rect.height / 2 - 80)}px`;
            style.left = `${rect.left - TOOLTIP_WIDTH - MARGIN}px`;
          }
          break;
        }
        default: {
          style.top = `${clampTop(rect.bottom + MARGIN)}px`;
          style.left = `${clampLeft(rect.left)}px`;
        }
      }

      setTooltipStyle(style);
    };

    computePosition();
    window.addEventListener('resize', computePosition);
    window.addEventListener('scroll', computePosition, true);
    return () => {
      window.removeEventListener('resize', computePosition);
      window.removeEventListener('scroll', computePosition, true);
    };
  }, [step]);

  return (
    <div 
      className="bg-white rounded-xl shadow-2xl p-4 sm:p-6 border-2 border-church-teal/20 overflow-y-auto"
      style={tooltipStyle}
      data-testid="tour-tooltip"
    >
      {/* Close button */}
      <button 
        onClick={onClose}
        className="absolute top-2 right-2 sm:top-3 sm:right-3 text-slate-400 hover:text-slate-600 transition-colors p-1"
        data-testid="tour-close-btn"
        aria-label="Close tour"
      >
        <X className="w-5 h-5" />
      </button>

      {/* Title */}
      {step.title && (
        <h3 className="text-lg sm:text-xl font-heading font-bold text-church-teal mb-2 sm:mb-3 pr-8">
          {step.title}
        </h3>
      )}
      
      {/* Content */}
      <div className="text-sm sm:text-base text-slate-600 mb-4 sm:mb-5 leading-relaxed">
        {step.content}
      </div>
      
      {/* Footer */}
      <div className="flex items-center justify-between pt-3 border-t border-slate-100 gap-2">
        <span className="text-xs sm:text-sm text-slate-400 font-medium whitespace-nowrap">
          Step {currentStep + 1} of {totalSteps}
        </span>
        <div className="flex gap-1 sm:gap-2">
          {currentStep > 0 && (
            <button
              onClick={onPrev}
              className="flex items-center gap-1 px-3 sm:px-4 py-2 text-xs sm:text-sm font-medium text-slate-600 hover:text-slate-800 hover:bg-slate-100 rounded-lg transition-colors"
              data-testid="tour-prev-btn"
            >
              <ChevronLeft className="w-4 h-4" />
              <span className="hidden xs:inline">Back</span>
            </button>
          )}
          <button
            onClick={onNext}
            className="flex items-center gap-1 px-3 sm:px-5 py-2 text-xs sm:text-sm font-medium bg-church-teal text-white rounded-lg hover:bg-church-teal-dark transition-colors"
            data-testid="tour-next-btn"
          >
            {currentStep === totalSteps - 1 ? 'Finish' : 'Next'}
            {currentStep < totalSteps - 1 && <ChevronRight className="w-4 h-4" />}
          </button>
        </div>
      </div>
    </div>
  );
};

const Spotlight = ({ target }) => {
  const [spotlightStyle, setSpotlightStyle] = useState(null);

  useEffect(() => {
    const computeSpotlight = () => {
      if (target === 'body') {
        setSpotlightStyle(null);
        return;
      }

      const element = document.querySelector(target);
      if (!element) {
        setSpotlightStyle(null);
        return;
      }

      const rect = element.getBoundingClientRect();
      // If target is offscreen (e.g., collapsed mobile sidebar), don't show a misplaced spotlight box
      const offscreen =
        rect.bottom < 0 ||
        rect.top > window.innerHeight ||
        rect.right < 0 ||
        rect.left > window.innerWidth ||
        rect.width === 0 ||
        rect.height === 0;

      if (offscreen) {
        setSpotlightStyle(null);
        return;
      }

      setSpotlightStyle({
        position: 'fixed',
        top: rect.top - 8,
        left: rect.left - 8,
        width: rect.width + 16,
        height: rect.height + 16,
        borderRadius: '12px',
        boxShadow: '0 0 0 9999px rgba(0, 0, 0, 0.5)',
        pointerEvents: 'none',
        transition: 'all 0.3s ease-in-out',
      });
    };

    computeSpotlight();
    window.addEventListener('resize', computeSpotlight);
    window.addEventListener('scroll', computeSpotlight, true);
    return () => {
      window.removeEventListener('resize', computeSpotlight);
      window.removeEventListener('scroll', computeSpotlight, true);
    };
  }, [target]);

  if (!spotlightStyle) {
    return <div className="fixed inset-0 bg-black/50 pointer-events-none" />;
  }

  return <div style={spotlightStyle} />;
};

// ==================== LANDING PAGE TOUR (Visitors) ====================
const landingSteps = [
  {
    target: 'body',
    content: (
      <div>
        <p className="mb-3">Welcome to the <strong>Alkimos Baptist Church</strong> community app!</p>
        <p>This interactive tour will walk you through all the features. Let&apos;s get started!</p>
      </div>
    ),
    title: 'Welcome! 👋',
    placement: 'center',
  },
  {
    target: '[data-testid="nav-give"]',
    content: (
      <div>
        <p className="mb-2">The <strong>Give</strong> button allows anyone to make online donations.</p>
        <p className="text-sm text-slate-500">No login required - visitors can give directly via Stripe.</p>
      </div>
    ),
    title: 'Online Giving',
    placement: 'bottom',
  },
  {
    target: '[data-testid="nav-signin"]',
    content: (
      <div>
        <p className="mb-2">Click <strong>Sign In</strong> to access member features.</p>
        <p className="text-sm text-slate-500">Church members can log in to view the community feed, join groups, and more.</p>
      </div>
    ),
    title: 'Member Sign In',
    placement: 'bottom',
  },
  {
    target: '[data-testid="get-started-btn"]',
    content: (
      <div>
        <p className="mb-2">New users can <strong>Join Our Community</strong> to create an account.</p>
        <p className="text-sm text-slate-500">Once registered, you&apos;ll have access to all member features.</p>
      </div>
    ),
    title: 'Join the Community',
    placement: 'top',
  },
];

// ==================== MEMBER TOUR (Regular church members) ====================
const memberSteps = [
  {
    target: 'body',
    content: (
      <div>
        <p className="mb-3">Welcome to your <strong>Church Community Hub</strong>!</p>
        <p>Let me show you around all the features available to you as a member.</p>
      </div>
    ),
    title: 'Welcome, Church Family!',
    placement: 'center',
  },
  {
    target: '[data-testid="nav-community"]',
    content: (
      <div>
        <p className="mb-2">The <strong>Community Feed</strong> is the heart of our church app.</p>
        <p className="text-sm text-slate-500">Share updates, encouragement, and stay connected with your church family.</p>
      </div>
    ),
    title: 'Community Feed',
    placement: 'bottom',
  },
  {
    target: '[data-testid="checkin-banner"]',
    content: (
      <div>
        <p className="mb-2">Tap <strong>&quot;I&apos;m Here!&quot;</strong> when you arrive at church on Sundays.</p>
        <p className="text-sm text-slate-500">See who else is at church today and let the community know you&apos;re present!</p>
      </div>
    ),
    title: 'Church Check-in',
    placement: 'bottom',
  },
  {
    target: '[data-testid="nav-prayer"]',
    content: (
      <div>
        <p className="mb-2"><strong>Prayer Groups</strong> let you share and support each other in prayer.</p>
        <p className="text-sm text-slate-500">Submit prayer requests (even anonymously) and pray for others.</p>
      </div>
    ),
    title: 'Prayer Groups',
    placement: 'bottom',
  },
  {
    target: '[data-testid="nav-groups"]',
    content: (
      <div>
        <p className="mb-2"><strong>Growth Groups</strong> are small groups for deeper fellowship.</p>
        <p className="text-sm text-slate-500">Join a group to connect with others who share your interests or live nearby.</p>
      </div>
    ),
    title: 'Growth Groups',
    placement: 'bottom',
  },
  {
    target: '[data-testid="nav-directory"]',
    content: (
      <div>
        <p className="mb-2">The <strong>Church Directory</strong> is your family photo book.</p>
        <p className="text-sm text-slate-500">Browse all church families and individuals with photos. Search by name or filter by families vs individuals.</p>
      </div>
    ),
    title: 'Church Directory',
    placement: 'bottom',
  },
  {
    target: '[data-testid="nav-music"]',
    content: (
      <div>
        <p className="mb-2">Browse our <strong>Worship Music Library</strong>.</p>
        <p className="text-sm text-slate-500">View lyrics, chords, and even see what songs are planned for upcoming services.</p>
      </div>
    ),
    title: 'Music Library',
    placement: 'bottom',
  },
  {
    target: '[data-testid="nav-calendar"]',
    content: (
      <div>
        <p className="mb-2">The <strong>Church Calendar</strong> shows all events in one place.</p>
        <p className="text-sm text-slate-500">View by month, see event details, and plan your participation.</p>
      </div>
    ),
    title: 'Church Calendar',
    placement: 'bottom',
  },
  {
    target: '[data-testid="nav-roster"]',
    content: (
      <div>
        <p className="mb-2">View your <strong>Service Roster</strong> assignments.</p>
        <p className="text-sm text-slate-500">See when you&apos;re serving, confirm/decline your role, and check who you&apos;re serving with.</p>
        <p className="text-sm text-orange-600 mt-2">💡 Going away? Tap <strong>&ldquo;Set availability&rdquo;</strong> on the Roster page to block out dates you can&apos;t serve.</p>
      </div>
    ),
    title: 'My Roster',
    placement: 'bottom',
  },
  {
    target: '[data-testid="user-menu-btn"]',
    content: (
      <div>
        <p className="mb-2">Tap your <strong>profile picture</strong> to manage your account.</p>
        <p className="text-sm text-slate-500">Update your profile, add family members and children, set blockout dates, and re-run this tour anytime.</p>
      </div>
    ),
    title: 'Your Profile',
    placement: 'bottom',
  },
  {
    target: '[data-testid="nav-give"]',
    content: (
      <div>
        <p className="mb-2">Support the church through <strong>Online Giving</strong>.</p>
        <p className="text-sm text-slate-500">Make one-time or recurring donations securely via Stripe.</p>
      </div>
    ),
    title: 'Give Online',
    placement: 'bottom',
  },
  {
    target: '[data-testid="notification-bell"]',
    content: (
      <div>
        <p className="mb-2">The <strong>Notification Bell</strong> shows important alerts.</p>
        <p className="text-sm text-slate-500">Service roster reminders, event updates, and announcements appear here.</p>
      </div>
    ),
    title: 'Notifications',
    placement: 'bottom',
  },
  {
    target: 'body',
    content: (
      <div>
        <p className="mb-3"><strong>That&apos;s the tour complete!</strong></p>
        <p className="mb-2">You now know how to:</p>
        <ul className="list-disc list-inside text-sm text-slate-600 space-y-1">
          <li>Check in when you arrive at church</li>
          <li>Connect with others in the Community Feed</li>
          <li>Browse the Church Directory</li>
          <li>Join Prayer Groups and Growth Groups</li>
          <li>View events, worship songs, and give online</li>
        </ul>
        <p className="mt-3 text-sm text-slate-500">God bless you!</p>
      </div>
    ),
    title: 'Tour Complete!',
    placement: 'center',
  },
];

// ==================== PASTORAL ADMIN TOUR (Full comprehensive tour) ====================
const pastoralAdminSteps = [
  // SECTION 1: Introduction
  {
    target: 'body',
    content: (
      <div>
        <p className="mb-3">Welcome, <strong>Pastoral Leader</strong>!</p>
        <p className="mb-2">This comprehensive tour covers <strong>everything</strong> in our church management system:</p>
        <ul className="list-disc list-inside text-sm text-slate-600 space-y-1">
          <li>Member CRM &amp; engagement tracking</li>
          <li>Ministry teams &amp; availability</li>
          <li>Music &amp; worship planning</li>
          <li>Finance &amp; donation management</li>
          <li>Children&apos;s ministry (Admin, Music, Chat)</li>
          <li>Weekly engagement reports</li>
        </ul>
      </div>
    ),
    title: 'Pastoral Admin Tour',
    placement: 'center',
  },
  
  // SECTION 2: Admin Dashboard Overview
  {
    target: '[data-testid="sidebar-dashboard"]',
    content: (
      <div>
        <p className="mb-2">The <strong>Dashboard</strong> gives you a bird&apos;s eye view.</p>
        <p className="text-sm text-slate-500">See member counts, donations, upcoming events, and quick actions all at a glance.</p>
      </div>
    ),
    title: 'Dashboard Overview',
    placement: 'right',
  },
  
  // SECTION 3: CRM
  {
    target: '[data-testid="sidebar-crm"]',
    content: (
      <div>
        <p className="mb-2">The <strong>Member CRM</strong> is your central hub for pastoral care.</p>
        <p className="text-sm text-slate-500 mb-2">Click any member to see their full profile:</p>
        <ul className="text-sm text-slate-600 space-y-1">
          <li>- Engagement tracking with traffic lights</li>
          <li>- Pastoral notes (prayer, follow-up, milestones)</li>
          <li>- Family, children, and group connections</li>
          <li>- Service history and giving records</li>
        </ul>
      </div>
    ),
    title: 'Member CRM',
    placement: 'right',
  },
  
  // SECTION 4: Pastoral Care
  {
    target: '[data-testid="sidebar-families"]',
    content: (
      <div>
        <p className="mb-2"><strong>Family Management</strong> links members into family units.</p>
        <p className="text-sm text-slate-500">Essential for children&apos;s ministry check-in and family communications.</p>
      </div>
    ),
    title: 'Family Units',
    placement: 'right',
  },
  {
    target: '[data-testid="sidebar-growth-groups"]',
    content: (
      <div>
        <p className="mb-2">Create and manage <strong>Growth Groups</strong>.</p>
        <p className="text-sm text-slate-500">Assign leaders, set meeting times, and track group membership.</p>
      </div>
    ),
    title: 'Growth Groups',
    placement: 'right',
  },

  // SECTION 5: Ministry Teams
  {
    target: '[data-testid="sidebar-teams"]',
    content: (
      <div>
        <p className="mb-2">Create custom <strong>Ministry Teams</strong> for your church.</p>
        <p className="text-sm text-slate-500 mb-2">Organize by category:</p>
        <ul className="text-sm text-slate-600 space-y-1">
          <li>- Worship, Tech/Sound, Welcome</li>
          <li>- Kids Ministry, Admin, and more</li>
          <li>- Add/remove members with roles</li>
        </ul>
      </div>
    ),
    title: 'Ministry Teams',
    placement: 'right',
  },
  
  // SECTION 6: Services & Roster
  {
    target: '[data-testid="sidebar-this-week"]',
    content: (
      <div>
        <p className="mb-2"><strong>This Week</strong> shows a quick snapshot of the upcoming Sunday service.</p>
        <p className="text-sm text-slate-500">See who&apos;s serving, the setlist, kids check-ins expected, and any pastoral notes — all in one screen.</p>
      </div>
    ),
    title: 'This Week',
    placement: 'right',
  },
  {
    target: '[data-testid="sidebar-matrix-planner"]',
    content: (
      <div>
        <p className="mb-2">The <strong>Matrix Planner</strong> shows the next 4 services side-by-side.</p>
        <p className="text-sm text-slate-500">Plan themes, order-of-service, roster, songs and kids check-ins across multiple Sundays in one view. Click the pencil to edit any service inline.</p>
      </div>
    ),
    title: 'Matrix Planner',
    placement: 'right',
  },
  {
    target: '[data-testid="sidebar-roster"]',
    content: (
      <div>
        <p className="mb-2"><strong>Service Roster</strong> replaces Planning Center!</p>
        <p className="text-sm text-slate-500">Schedule who&apos;s serving each Sunday - worship team, sound, welcome team, etc.</p>
        <p className="text-sm text-church-teal mt-2">No more per-assignment emails — use Roster Emails to send smart batched summaries.</p>
      </div>
    ),
    title: 'Service Roster',
    placement: 'right',
  },
  {
    target: '[data-testid="sidebar-roster-emails"]',
    content: (
      <div>
        <p className="mb-2"><strong>Smart Roster Emails</strong> — send ONE consolidated email per member.</p>
        <p className="text-sm text-slate-500 mb-2">Instead of 6 separate emails for 6 slots, members get a single summary grouped by team and role.</p>
        <ul className="text-sm text-slate-600 space-y-1">
          <li>- Auto-detects which slots haven&apos;t been emailed yet</li>
          <li>- Add a custom message at the top</li>
          <li>- Tracks who&apos;s been notified</li>
        </ul>
      </div>
    ),
    title: 'Smart Roster Emails',
    placement: 'right',
  },
  {
    target: '[data-testid="sidebar-setlists"]',
    content: (
      <div>
        <p className="mb-2"><strong>Worship Planning</strong> creates setlists for services.</p>
        <p className="text-sm text-slate-500">Build your service plan, add songs from the library, assign worship team members, and launch projector mode.</p>
      </div>
    ),
    title: 'Worship Planning',
    placement: 'right',
  },

  // SECTION 7: Availability
  {
    target: '[data-testid="sidebar-availability"]',
    content: (
      <div>
        <p className="mb-2"><strong>Availability Blockouts</strong> track when members are unavailable.</p>
        <p className="text-sm text-slate-500">Members or admins can set date ranges for holidays, travel, or other commitments. Helps with roster planning!</p>
      </div>
    ),
    title: 'Availability Blockouts',
    placement: 'right',
  },
  
  // SECTION 8: Kids Ministry
  {
    target: '[data-testid="sidebar-kids-admin"]',
    content: (
      <div>
        <p className="mb-2">The <strong>ABC Kids Dashboard</strong> is your kids ministry hub.</p>
        <p className="text-sm text-slate-500">View all registered children, allergy alerts, and quick-access to check-in, kids music, and team chat.</p>
      </div>
    ),
    title: 'Kids Dashboard',
    placement: 'right',
  },
  {
    target: '[data-testid="sidebar-kids-checkin"]',
    content: (
      <div>
        <p className="mb-2"><strong>Kids Check-in</strong> for children&apos;s ministry.</p>
        <p className="text-sm text-slate-500">Register children with parent links, track allergies, and run quick check-in/check-out on Sundays.</p>
      </div>
    ),
    title: 'Kids Check-in',
    placement: 'right',
  },
  {
    target: '[data-testid="sidebar-kids-music"]',
    content: (
      <div>
        <p className="mb-2"><strong>Kids Music</strong> is a dedicated song library for children.</p>
        <p className="text-sm text-slate-500">Add action songs, worship songs, and bible songs with lyrics, YouTube links, age groups, and movement instructions.</p>
      </div>
    ),
    title: 'Kids Music Library',
    placement: 'right',
  },
  {
    target: '[data-testid="sidebar-kids-chat"]',
    content: (
      <div>
        <p className="mb-2"><strong>Kids Team Chat</strong> keeps your volunteers connected.</p>
        <p className="text-sm text-slate-500 mb-2">Features include:</p>
        <ul className="text-sm text-slate-600 space-y-1">
          <li>- Create channels per team or session</li>
          <li>- Real-time messaging</li>
          <li>- Post coloured sticky notes for recaps</li>
          <li>- Pin important messages</li>
        </ul>
      </div>
    ),
    title: 'Kids Team Chat',
    placement: 'right',
  },

  // SECTION 9: Music
  {
    target: '[data-testid="sidebar-worship-planning"]',
    content: (
      <div>
        <p className="mb-2"><strong>Worship Planning</strong> is your end-to-end Sunday service builder.</p>
        <p className="text-sm text-slate-500">Plan songs by service, assign instrument-specific chords, add guest musicians, and launch projector mode on Sunday morning.</p>
      </div>
    ),
    title: 'Worship Planning',
    placement: 'right',
  },
  {
    target: '[data-testid="sidebar-music"]',
    content: (
      <div>
        <p className="mb-2">Manage the <strong>Music Library</strong>.</p>
        <p className="text-sm text-slate-500">Add new songs with lyrics, chords, tags, and BPM. Members can browse but only admins can edit.</p>
      </div>
    ),
    title: 'Music Library',
    placement: 'right',
  },
  
  // SECTION 10: Finance
  {
    target: '[data-testid="sidebar-donations"]',
    content: (
      <div>
        <p className="mb-2">Record <strong>Cash &amp; Check Donations</strong>.</p>
        <p className="text-sm text-slate-500">Manual entry for offerings collected during services. Generates tax receipts automatically.</p>
      </div>
    ),
    title: 'Donation Tracking',
    placement: 'right',
  },
  {
    target: '[data-testid="sidebar-online-giving"]',
    content: (
      <div>
        <p className="mb-2"><strong>Online Giving</strong> shows Stripe transactions.</p>
        <p className="text-sm text-slate-500">All donations made through the website&apos;s Give page appear here automatically.</p>
      </div>
    ),
    title: 'Online Donations',
    placement: 'right',
  },
  {
    target: '[data-testid="sidebar-banking"]',
    content: (
      <div>
        <p className="mb-2"><strong>Bank Deposits</strong> for financial reconciliation.</p>
        <p className="text-sm text-slate-500">Group donations together when depositing at the bank. Creates an audit trail.</p>
      </div>
    ),
    title: 'Bank Deposits',
    placement: 'right',
  },
  {
    target: '[data-testid="sidebar-reports"]',
    content: (
      <div>
        <p className="mb-2">Generate <strong>Financial Reports</strong>.</p>
        <p className="text-sm text-slate-500">Monthly summaries, giving trends, and reports for church board meetings.</p>
      </div>
    ),
    title: 'Reports',
    placement: 'right',
  },
  
  // SECTION 11: Content Management
  {
    target: '[data-testid="sidebar-events"]',
    content: (
      <div>
        <p className="mb-2">Create and manage <strong>Church Events</strong>.</p>
        <p className="text-sm text-slate-500">Add events to the calendar, track RSVPs, and send reminders to attendees.</p>
      </div>
    ),
    title: 'Events',
    placement: 'right',
  },
  {
    target: '[data-testid="sidebar-announcements"]',
    content: (
      <div>
        <p className="mb-2">Post <strong>Announcements</strong> to the community.</p>
        <p className="text-sm text-slate-500">Important notices appear prominently in the community feed for all members.</p>
      </div>
    ),
    title: 'Announcements',
    placement: 'right',
  },
  
  // SECTION 12: System
  {
    target: '[data-testid="sidebar-weekly-report"]',
    content: (
      <div>
        <p className="mb-2"><strong>Weekly Engagement Reports</strong> auto-generate every Monday morning.</p>
        <p className="text-sm text-slate-500 mb-2">Each report includes:</p>
        <ul className="text-sm text-slate-600 space-y-1">
          <li>- New members and engagement changes</li>
          <li>- Follow-up needed list</li>
          <li>- Attendance, kids check-ins, giving totals</li>
          <li>- Upcoming blockouts and pastoral notes</li>
        </ul>
        <p className="text-sm text-church-teal mt-2">Reports are emailed automatically to all admins!</p>
      </div>
    ),
    title: 'Weekly Reports',
    placement: 'right',
  },
  {
    target: '[data-testid="sidebar-audit"]',
    content: (
      <div>
        <p className="mb-2">The <strong>Audit Log</strong> tracks all admin actions.</p>
        <p className="text-sm text-slate-500">See who did what and when - essential for accountability and compliance.</p>
      </div>
    ),
    title: 'Audit Trail',
    placement: 'right',
  },
  {
    target: '[data-testid="sidebar-access-code"]',
    content: (
      <div>
        <p className="mb-2"><strong>Sign-up Access Code</strong> protects new registrations.</p>
        <p className="text-sm text-slate-500 mb-2">Only people who enter the current code can create accounts. Rotate, copy, or disable sign-ups any time.</p>
        <p className="text-sm text-church-teal">Share the code on Sunday or in personal invites to gate access to the church family.</p>
      </div>
    ),
    title: 'Sign-up Access Code',
    placement: 'right',
  },
  
  // SECTION 13: Summary
  {
    target: 'body',
    content: (
      <div>
        <p className="mb-3"><strong>Comprehensive tour complete!</strong></p>
        <p className="mb-2 font-medium">Key features at your fingertips:</p>
        <div className="grid grid-cols-2 gap-2 text-sm text-slate-600 mb-3">
          <div>Member CRM &amp; profiles</div>
          <div>Custom ministry teams</div>
          <div>Traffic light engagement</div>
          <div>Availability blockouts</div>
          <div>48-hour auto reminders</div>
          <div>Kids check-in + music + chat</div>
          <div>Full financial tracking</div>
          <div>Weekly engagement reports</div>
          <div>Church directory</div>
          <div>Complete audit trail</div>
        </div>
        <p className="text-sm text-church-teal">You can restart this tour anytime using the &quot;Take a Tour&quot; button.</p>
      </div>
    ),
    title: 'You&apos;re Ready!',
    placement: 'center',
  },
];

// Get steps based on tour type
const getSteps = (tourType) => {
  switch (tourType) {
    case 'pastoral_admin':
      return pastoralAdminSteps;
    case 'member':
      return memberSteps;
    case 'landing':
    default:
      return landingSteps;
  }
};

export const AppTour = ({ tourType = 'landing', run = false, onComplete }) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [isRunning, setIsRunning] = useState(run);

  const steps = getSteps(tourType);

  useEffect(() => {
    setIsRunning(run);
    if (run) setCurrentStep(0);
  }, [run]);

  const handleNext = useCallback(() => {
    if (currentStep < steps.length - 1) {
      setCurrentStep(currentStep + 1);
    } else {
      setIsRunning(false);
      if (onComplete) onComplete();
    }
  }, [currentStep, steps.length, onComplete]);

  const handlePrev = useCallback(() => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  }, [currentStep]);

  const handleClose = useCallback(() => {
    setIsRunning(false);
    if (onComplete) onComplete();
  }, [onComplete]);

  // Handle keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (!isRunning) return;
      if (e.key === 'Escape') handleClose();
      if (e.key === 'ArrowRight' || e.key === 'Enter') handleNext();
      if (e.key === 'ArrowLeft') handlePrev();
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isRunning, handleNext, handlePrev, handleClose]);

  if (!isRunning) return null;

  const currentStepData = steps[currentStep];

  return (
    <TourOverlay>
      <Spotlight target={currentStepData.target} />
      <TourTooltip
        step={currentStepData}
        currentStep={currentStep}
        totalSteps={steps.length}
        onNext={handleNext}
        onPrev={handlePrev}
        onClose={handleClose}
      />
    </TourOverlay>
  );
};

// Tour trigger button component
export const TourButton = ({ onClick, variant = 'default', label = 'Take a Tour' }) => {
  const baseStyles = "flex items-center gap-2 font-medium transition-all duration-200";
  
  const variants = {
    default: "px-4 py-2 bg-church-teal text-white rounded-lg hover:bg-church-teal-dark shadow-md hover:shadow-lg",
    outline: "px-4 py-2 border-2 border-church-teal text-church-teal rounded-lg hover:bg-church-teal hover:text-white",
    subtle: "px-3 py-1.5 text-church-teal hover:bg-church-teal/10 rounded-md text-sm",
    floating: "fixed bottom-6 right-6 px-5 py-3 bg-church-teal text-white rounded-full shadow-xl hover:shadow-2xl hover:scale-105 z-50",
    header: "px-3 py-1.5 text-church-teal hover:text-church-teal-dark hover:bg-church-teal/10 rounded-full text-sm",
  };

  return (
    <button
      onClick={onClick}
      className={`${baseStyles} ${variants[variant]}`}
      data-testid="start-tour-btn"
    >
      <HelpCircle className="w-5 h-5" />
      <span>{label}</span>
    </button>
  );
};

export default AppTour;
